/**
 * TranslationFields — the shared per-locale editor for admin-authored text.
 *
 * One component, dropped into every admin editor that owns translatable
 * content. It renders a locale tab strip over one input per translatable field,
 * with the English shown read-only as the reference the translator is working
 * from.
 *
 * It handles both shapes the overlay supports without the caller caring which:
 *
 *   - a scalar column        → path 'name'
 *   - a path into a JSON blob → path 'buttons_json.#save-pdf.label'
 *
 * The caller supplies the paths and their English values; this component knows
 * nothing about entity schemas. That is what keeps it to one component across
 * seven editors instead of seven variants.
 *
 * State is deliberately LIFTED: the parent owns `values` and passes `onChange`,
 * so translations save in the same submit as the English rather than as a
 * separate "did you also press the other Save?" step. `collectTranslations()`
 * below is what a parent hands to saveTranslations().
 */

import React, { lazy, Suspense, useEffect, useMemo, useState } from 'react';
import { Languages, Loader2 } from 'lucide-react';
import DOMPurify from 'dompurify';
import {
  fetchTranslations,
  type TranslatableLocale,
  type TranslationValues,
} from '../../services/translationsApi';

// Jodit is heavy — only pulled in when a field actually asks for rich text.
const RichTextEditor = lazy(() => import('./RichTextEditor'));

export interface TranslatableField {
  /** Column name, or dotted path into a JSON blob. */
  path: string;
  /** What to call this field in the editor ("Label", "Product name"). */
  label: string;
  /** The English the translator is working from. Shown read-only. */
  english: string;
  /** Render a textarea instead of an input (rich/long copy). */
  multiline?: boolean;
  /**
   * Render the semantic rich-text editor (HTML in, HTML out) — for fields whose
   * English is itself HTML (output sections). The English reference renders as
   * formatted HTML rather than raw markup.
   */
  richText?: boolean;
  /** Where images inserted into a rich-text translation upload to. */
  imageUpload?: { kind: 'section-image'; retailerId: number };
}

interface Props {
  entityType: string;
  /** Row id. Null for an unsaved row — the editor renders a hint instead. */
  entityId: number | null;
  fields: TranslatableField[];
  /** Current edits, owned by the parent: path → locale → value. */
  values: TranslationValues;
  onChange: (values: TranslationValues) => void;
  /** Heading text. Defaults to "Translations". */
  title?: string;
}

/** Read one cell out of the lifted state. */
const cell = (values: TranslationValues, path: string, locale: string): string =>
  values?.[path]?.[locale] ?? '';

/** Immutably set one cell. */
function setCell(
  values: TranslationValues,
  path: string,
  locale: string,
  value: string,
): TranslationValues {
  return { ...values, [path]: { ...(values[path] ?? {}), [locale]: value } };
}

export const TranslationFields: React.FC<Props> = ({
  entityType,
  entityId,
  fields,
  values,
  onChange,
  title = 'Translations',
}) => {
  const [locales, setLocales] = useState<TranslatableLocale[]>([]);
  const [active, setActive] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Load the registry + any stored values for this row. Runs once per row: the
  // parent owns edits from then on, so re-fetching would discard them.
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetchTranslations(entityType, entityId === null ? [] : [entityId])
      .then((res) => {
        if (cancelled) return;
        setLocales(res.locales);
        setActive((prev) => prev || res.locales[0]?.code || '');
        if (entityId !== null) {
          const stored = res.translations[String(entityId)];
          if (stored) onChange(stored);
        }
      })
      .catch((e) => { if (!cancelled) setError(e instanceof Error ? e.message : 'Could not load translations'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
    // Deps are deliberately just the row identity. `onChange` is a fresh
    // closure on every parent render, so including it would refetch on each
    // keystroke and clobber the edits in progress. (No eslint suppression here:
    // react-hooks rules are not loaded in this project's config, and a disable
    // comment for an unloaded rule is itself a build error.)
  }, [entityType, entityId]);

  const translatable = useMemo(
    () => fields.filter((f) => f.path.trim() !== ''),
    [fields],
  );

  if (loading) {
    return (
      <div className="text-muted small d-flex align-items-center gap-2 py-2">
        <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} /> Loading translations…
      </div>
    );
  }

  if (error) {
    return <div className="alert alert-warning py-2 small mb-0">{error}</div>;
  }

  // No other languages configured — say so plainly rather than rendering an
  // empty tab strip that looks broken.
  if (locales.length === 0) {
    return (
      <div className="text-muted small py-2">
        No other languages are active. Add one under <strong>Locales</strong> to translate this content.
      </div>
    );
  }

  if (translatable.length === 0) return null;

  return (
    <div className="border rounded-3 p-3 mt-3 bg-light-subtle">
      <div className="d-flex align-items-center gap-2 mb-2">
        <Languages size={16} className="text-secondary" />
        <span className="fw-semibold small">{title}</span>
      </div>

      {entityId === null ? (
        <p className="text-muted small mb-0">
          Save this first — translations attach to the saved row.
        </p>
      ) : (
        <>
          <ul className="nav nav-pills nav-pills-sm gap-1 mb-3">
            {locales.map((l) => (
              <li className="nav-item" key={l.code}>
                <button
                  type="button"
                  className={`nav-link py-1 px-2 small ${active === l.code ? 'active' : ''}`}
                  onClick={() => setActive(l.code)}
                >
                  {l.nativeName}
                  <span className="text-muted ms-1">({l.code})</span>
                </button>
              </li>
            ))}
          </ul>

          {translatable.map((f) => (
            <div className="mb-3" key={f.path}>
              <label className="form-label small fw-semibold mb-1">{f.label}</label>
              {/* English first and read-only: the translator needs the source
                  in front of them, and it must not be editable here — it lives
                  in the field above, which is the real record. */}
              {f.richText ? (
                <div
                  className="small text-muted mb-1 px-2 py-1 bg-body-secondary rounded border overflow-auto"
                  style={{ maxHeight: 220 }}
                  dangerouslySetInnerHTML={{ __html: f.english ? DOMPurify.sanitize(f.english, { ADD_ATTR: ['target'] }) : '<em>(empty)</em>' }}
                />
              ) : (
                <div className="small text-muted mb-1 px-2 py-1 bg-body-secondary rounded border">
                  {f.english || <em>(empty)</em>}
                </div>
              )}
              {f.richText ? (
                <Suspense fallback={<div className="text-muted small py-3"><Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} /> Loading editor…</div>}>
                  {/* Re-key per locale: the editor captures its initial value once. */}
                  <RichTextEditor
                    key={`${f.path}:${active}`}
                    semantic
                    imageUpload={f.imageUpload}
                    value={cell(values, f.path, active)}
                    onChange={(html) => onChange(setCell(values, f.path, active, html))}
                  />
                </Suspense>
              ) : f.multiline ? (
                <textarea
                  className="form-control form-control-sm"
                  rows={4}
                  value={cell(values, f.path, active)}
                  placeholder={`${f.english || f.label} — in ${active}`}
                  onChange={(e) => onChange(setCell(values, f.path, active, e.target.value))}
                />
              ) : (
                <input
                  className="form-control form-control-sm"
                  value={cell(values, f.path, active)}
                  placeholder={`${f.english || f.label} — in ${active}`}
                  onChange={(e) => onChange(setCell(values, f.path, active, e.target.value))}
                />
              )}
            </div>
          ))}

          <p className="text-muted mb-0" style={{ fontSize: 12 }}>
            Leave a field blank to fall back to English.
          </p>
        </>
      )}
    </div>
  );
};

export default TranslationFields;
